/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import RenderAllAssignments from '@/components/RenderAllAssignments';
import { useSyncForAvatar } from '@/service/api/box/Hooks';
import { models } from '@/service/db/Setup';
import useAsyncData from '@/utils/AsyncData';
import { AvatarRecord, TaskRecord, WorkerRecord } from '@karya/core';
import { useCallback } from 'react';

/** Props for rendering main task rendering phase. */
export type MainTaskPhaseProps = {
  task: TaskRecord;
  activeAvatar: AvatarRecord;
  activeWorker: WorkerRecord;
  scrollToBottom: () => void;
};

/** Render the assignments for Main Task Phase. */
export function MainTaskPhase(props: MainTaskPhaseProps) {
  const syncAssignments = useSyncForAvatar();
  const assignmentsDb = useAsyncData(
    () =>
      models.basic
        .getAllFromIndex('microtask_assignment', 'avatar_id', props.activeAvatar.id)
        .unwrapResult()
        .getOrThrow(),
    {
      skip: !props.activeAvatar.id,
      deps: [props.activeAvatar.id],
    },
  );

  /**  Handler to be called when an assignment is completed. */
  const handleAssignmentCompletion = useCallback(async () => {
    assignmentsDb.triggerFetch();

    // TODO: Replace with manual sync for submission
    // FIXME: Remove sync from here
    // Ideally we don't want to sync immeadiately after submission of each
    // assignment it is not efficient
    await syncAssignments({
      avatar: props.activeAvatar,
      submitAssignments: true,
      getNewAssignments: true,
    });

    assignmentsDb.triggerFetch();
  }, [props.activeAvatar, assignmentsDb, syncAssignments]);

  if (assignmentsDb.error) {
    return <div>Error loading assignments: {assignmentsDb.error.message}</div>;
  }

  if (assignmentsDb.isLoading && !assignmentsDb.data) {
    return <div>Loading assignments...</div>;
  }

  return (
    <RenderAllAssignments
      assignments={assignmentsDb.data ?? []}
      task={props.task}
      activeWorker={props.activeWorker}
      onComplete={handleAssignmentCompletion}
      scrollToBottom={props.scrollToBottom}
    />
  );
}
